import { useDrag } from "react-dnd";
import {
  CalendarDndDropResult,
  CalendarDndItem,
  CalendarDndItemTypes,
} from "../../../../../constants/calendarDndTypes";
import { useCallback, useState } from "react";
import { isInPersonSession } from "utils/online-offline/isInPersonSession";
import { useDispatch } from "react-redux";
import { setFocusedSession } from "store/calendarSidebarSlice";
import switchSessionHeldPlace from "services/online-offline/switchSessionHeldPlace";
import rescheduleAppointment from "services/scheduler/rescheduleAppointment";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

interface UseCalendarAppointmentDragProps {
  appointment: AppointmentWithSeriesInfo & { invoice?: InvoiceData };
  handleRescheduleError: (string) => void;
  openConfirmDndModal: () => void;
}

const getSessionEndTime = (appointment, newStart) => {
  const duration =
    new Date(appointment.appointment_end).getTime() -
    new Date(appointment.appointment_start).getTime();

  const newEnd = new Date(newStart).getTime() + duration;
  return new Date(newEnd).toISOString();
};

const useCalendarAppointmentDrag = ({
  appointment,
  handleRescheduleError,
  openConfirmDndModal,
}: UseCalendarAppointmentDragProps) => {
  const dispatch = useDispatch();

  const [isBeingRescheduledViaDnd, setIsBeingRescheduledViaDnd] =
    useState<boolean>(false);

  const [onConfirmDnd, setOnConfirmDnd] = useState<(() => void) | null>(null);
  const [onCancelDnd, setOnCancelDnd] = useState<(() => void) | null>(null);

  const { practiceOwnerOrchidUserId } = useTeamContext();

  const rescheduleSession = useCallback(
    async (startISO, onSuccess, onError) => {
      setIsBeingRescheduledViaDnd(true);

      const telehealth = !isInPersonSession(appointment);
      const externalLink = appointment.external_meeting_link;

      const new_appointment_start = startISO;
      const new_appointment_end = getSessionEndTime(appointment, startISO);

      dispatch(
        setFocusedSession({
          ...appointment,
          appointment_start: new_appointment_start,
          appointment_end: new_appointment_end,
        }),
      );

      try {
        await switchSessionHeldPlace({
          appointmentId: appointment.appointment_id,
          placeValue: !telehealth
            ? "outside"
            : telehealth && externalLink
            ? "outside"
            : "orchid",
          appointmentHeldPlace: !telehealth
            ? "in-person"
            : telehealth && externalLink
            ? "external-platform"
            : null,
          externalMeetingLink: telehealth && externalLink ? externalLink : null,
          ...(practiceOwnerOrchidUserId && { onBehalfOf: practiceOwnerOrchidUserId }),
        });
        const rescheduledAppointment = await rescheduleAppointment({
          appointments: [
            {
              appointment_id: appointment.appointment_id,
              new_appointment_start,
              new_appointment_end,
            },
          ],
          force_book_over_blocked: true,
          ...(practiceOwnerOrchidUserId && { onBehalfOf: practiceOwnerOrchidUserId }),
        });
        dispatch(
          setFocusedSession({ ...appointment, ...rescheduledAppointment }),
        );
        onSuccess();
      } catch (error) {
        onError();
        dispatch(setFocusedSession(appointment));
        if (error instanceof Error && error.message) {
          handleRescheduleError(error.message);
        } else {
          handleRescheduleError("Could not reschedule appointment");
        }
      } finally {
        setIsBeingRescheduledViaDnd(false);
      }
    },
    [appointment, dispatch, handleRescheduleError, practiceOwnerOrchidUserId],
  );

  const [{ isDragging, isAnotherAppointmentBeingDragged }, drag, dragPreview] =
    useDrag<
      CalendarDndItem,
      CalendarDndDropResult,
      { isDragging: boolean; isAnotherAppointmentBeingDragged: boolean }
    >(
      () => ({
        type: CalendarDndItemTypes.APPOINTMENT,
        item: {
          appointment,
        },
        collect: (monitor) => ({
          isDragging: !!monitor.isDragging(),
          isAnotherAppointmentBeingDragged:
            monitor.getItemType() === CalendarDndItemTypes.APPOINTMENT &&
            monitor.getItem().appointment.appointment_id !==
              appointment.appointment_id,
        }),
        end(_draggedItem, monitor) {
          const result = monitor.getDropResult();
          if (result?.isValid) {
            const { rescheduledAppointment, onSuccess, onError } = result;
            setOnConfirmDnd(
              () => () =>
                rescheduleSession(
                  rescheduledAppointment.appointment_start,
                  onSuccess,
                  onError,
                ),
            );
            setOnCancelDnd(() => onError);
            openConfirmDndModal();
          }
        },
      }),
      [appointment, rescheduleSession, openConfirmDndModal],
    );
  return {
    drag,
    dragPreview,
    isDragging,
    isAnotherAppointmentBeingDragged,
    isBeingRescheduledViaDnd,
    onConfirmDnd,
    onCancelDnd,
  };
};

export default useCalendarAppointmentDrag;
